// library.ts — the article ("beat") library ported from the reduced-version app.
// Self-contained: content lives in src/data/library.json; read + saved state in
// localStorage. Nothing here touches the programme/progress store, so the whole
// feature can be removed by deleting these files + the tab wiring.
import data from '@/data/library.json';

export interface Article {
  id: string;
  type: 'beat' | 'blog';
  rowNumber: number | null;
  title: string;
  subtext: string;
  duration: string;
  modes: string[];
  tags: string[];
  primaryShelf: string;
  body: string;
  url?: string;
}

// Only "beats" (in-app readers); the source also had external "blog" links, but
// the content set is all beats.
export const ALL_ENTRIES: Article[] = (data as unknown as Article[]).filter((e) => e.type === 'beat');

export const TAG_VALUES = [
  'Calm',
  'Hold',
  'Respond',
  'Rebuild',
  'Co-parenting',
  'Court',
  'Hard week',
  'Work',
  'Health',
] as const;

// ── category colour tokens (single source of truth) ─────────────────────────
// One entry per shelf/theme. Each carries a derived family so colour is never
// hard-coded at the call site:
//   base   — the brand accent (left rule / border / motif seed)
//   kicker — a DARKENED variant tuned to clear WCAG AA (≥4.5:1) for the small,
//            bold, uppercase shelf label on a white card. The raw `base` hues
//            are too light for text (≈2.5:1) — never use `base` for the label.
//   wash   — a 7% accent tint, painted over the cream/white card as a faint
//            category wash.
export interface CategoryTokens {
  base: string;
  kicker: string;
  wash: string;
}
export const CATEGORY_TOKENS: Record<string, CategoryTokens> = {
  Calm: { base: '#5D8A9E', kicker: '#356275', wash: 'rgba(93,138,158,0.07)' },
  Hold: { base: '#8B7AA8', kicker: '#5A4A78', wash: 'rgba(139,122,168,0.07)' },
  Respond: { base: '#6B8E6E', kicker: '#3E6B4A', wash: 'rgba(107,142,110,0.07)' },
  Rebuild: { base: '#C17D40', kicker: '#95591F', wash: 'rgba(193,125,64,0.07)' },
  Court: { base: '#5A4218', kicker: '#5A4218', wash: 'rgba(90,66,24,0.07)' },
  Work: { base: '#4A3A5E', kicker: '#4A3A5E', wash: 'rgba(74,58,94,0.07)' },
  Health: { base: '#2C4A59', kicker: '#2C4A59', wash: 'rgba(44,74,89,0.07)' },
  'Co-parenting': { base: '#6B8E6E', kicker: '#3E6B4A', wash: 'rgba(107,142,110,0.07)' },
  'Hard week': { base: '#8A6F4E', kicker: '#5E4A30', wash: 'rgba(138,111,78,0.07)' },
};
const FALLBACK_TOKENS: CategoryTokens = {
  base: 'var(--amber-deep)',
  kicker: 'var(--amber-deep)',
  wash: 'rgba(155,103,18,0.07)',
};
// Resolve tokens by shelf/tag key, or by an entry's primaryShelf.
export const categoryTokens = (key: string): CategoryTokens => CATEGORY_TOKENS[key] || FALLBACK_TOKENS;
const entryKey = (e: Article): string => e.primaryShelf;

// Backwards-compatible accessors. `pillarColor` returns the base accent (rule /
// border); `categoryKicker` returns the AA-safe label colour; `categoryWash`
// the faint card tint. All read from CATEGORY_TOKENS — no scattered hex.
export const pillarColor = (e: Article): string => categoryTokens(entryKey(e)).base;
export const categoryKicker = (e: Article): string => categoryTokens(entryKey(e)).kicker;
export const categoryWash = (e: Article): string => categoryTokens(entryKey(e)).wash;

// Plain-English display labels for the (clinically-worded) themes. The underlying
// tag KEYS are unchanged — they still key article.tags, entriesForTag, and
// CATEGORY_TOKENS — only what the reader sees changes. Edit these freely.
export const TAG_LABEL: Record<string, string> = {
  Calm: 'Calming Trauma',
  Hold: 'Boundaries',
  Respond: 'Co-Parenting & Conflict',
  Rebuild: 'Rebuilding Life',
  'Co-parenting': 'Co-Parenting',
  Court: 'Court & Legal',
  'Hard week': 'A Hard Week',
  Work: 'At Work',
  Health: 'Health',
};
export const tagLabel = (tag: string): string => TAG_LABEL[tag] || tag;

// The four core content themes (these carry the Phase 2 motifs).
export const PILLARS = ['Calm', 'Hold', 'Respond', 'Rebuild'] as const;

// The Library landing is a grid of "libraries" — one tile per category. The four
// themes lead; the three situational categories (Court / Work / Health) follow as
// plainer tiles (no motif, Phase 1 tint only). Every article carries at least one
// of these tags, so the grid reaches the whole library.
export const LIBRARIES = ['Calm', 'Hold', 'Respond', 'Rebuild', 'Court', 'Work', 'Health'] as const;

const PILLAR_BLURB: Record<string, string> = {
  Calm: 'Ground & settle the body',
  Hold: 'Distance, no-contact, detaching',
  Respond: 'The ex, children, conflict',
  Rebuild: 'Heal & move forward',
  Court: 'Solicitors, hearings, the system',
  Work: 'Staying steady on the job',
  Health: 'Body, sleep & symptoms',
};
export const pillarBlurb = (tag: string): string => PILLAR_BLURB[tag] || '';

export const getEntryById = (id: string): Article | undefined => ALL_ENTRIES.find((e) => e.id === id);
export const durationMins = (e: Article): number => {
  const m = parseInt(String(e.duration), 10);
  return Number.isFinite(m) ? m : 99;
};

// A small, always-useful "Start here" shelf — the shortest grounding tools.
export const TOP_SHELF: Article[] = [...ALL_ENTRIES]
  .filter((e) => e.tags.includes('Calm') || durationMins(e) <= 1)
  .sort((a, b) => durationMins(a) - durationMins(b))
  .slice(0, 4);
const TOP_SHELF_IDS = new Set(TOP_SHELF.map((e) => e.id));

export function entriesForTag(tag: string): Article[] {
  if (tag === 'Saved') return ALL_ENTRIES.filter((e) => isSaved(e.id));
  let pool = tag === 'All' ? ALL_ENTRIES : ALL_ENTRIES.filter((e) => e.tags.includes(tag));
  // On the default view, the top shelf already surfaces a few — don't repeat them.
  if (tag === 'All') pool = pool.filter((e) => !TOP_SHELF_IDS.has(e.id));
  return pool;
}

// ── read + saved state (localStorage) ───────────────────────────────────────
export const LIBRARY_EVENT = 'renurture_library_changed';
const bump = () => {
  try {
    window.dispatchEvent(new Event(LIBRARY_EVENT));
  } catch {
    /* SSR / no window */
  }
};
const load = (key: string): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) || '[]') as string[]);
  } catch {
    return new Set();
  }
};
const store = (key: string, s: Set<string>) => {
  try {
    localStorage.setItem(key, JSON.stringify([...s]));
  } catch {
    /* ignore quota / private mode */
  }
};

const READ_KEY = 'renurture_library_read';
export const isRead = (id: string): boolean => load(READ_KEY).has(id);
export function markRead(id: string): void {
  const s = load(READ_KEY);
  if (!s.has(id)) {
    s.add(id);
    store(READ_KEY, s);
    bump();
  }
}

const SAVE_KEY = 'renurture_library_saved';
export const isSaved = (id: string): boolean => load(SAVE_KEY).has(id);
export function toggleSaved(id: string): void {
  const s = load(SAVE_KEY);
  if (s.has(id)) s.delete(id);
  else s.add(id);
  store(SAVE_KEY, s);
  bump();
}
export const savedCount = (): number => load(SAVE_KEY).size;
// Saved articles as full entries, newest first — used by the "Your stuff → Saved"
// viewer (the Library no longer has its own Saved tab).
export function savedEntries(): Article[] {
  const ids = [...load(SAVE_KEY)].reverse();
  return ids.map((id) => getEntryById(id)).filter((e): e is Article => !!e);
}
